import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
interface Patient {
  id: string;
  name: string;
}

interface Service {
  id: string;
  name: string;
  description: string;
  price: string;
  duration_minutes: number;
  status: boolean;
  service_type: string | null;
  clinic: number;
}

type AppointmentStatus = "confirmed" | "pending" | "completed" | "cancelled";

interface Appointment {
  id: string;
  title: string; // Used here as the 'Service' (Servicio)
  start_time: string; // ISO 8601 string, e.g., '2025-10-28T10:40:00-06:00'
  end_time: string;
  notes: string | null;
  status: AppointmentStatus;
  patient: Patient;
  is_recurrent: boolean;
  recurrence_period: string | null;
  office: string | null;
  service: Service | null;
}

export interface AppointmentsApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Appointment[];
  confirmed_appointments_count: number;
  pending_appointments_count: number;
  cancelled_appointments_count: number;
  completed_appointments_count: number;
}

/**
 * Formats an ISO date string into a localized, user-friendly string
 * (e.g., "Hoy, 4:00 PM" or "28/Oct/2025, 10:40 AM").
 * @param isoDateString The appointment start_time string.
 * @returns A formatted date and time string.
 */
const formatAppointmentDateTime = (isoDateString: string): string => {
  const date = new Date(isoDateString);
  const now = new Date();

  // Helper to check if it's today
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  // Helper to check if it's tomorrow
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear();

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const timeString = date.toLocaleTimeString("es-ES", timeOptions);

  if (isToday) {
    return `Hoy, ${timeString}`;
  }

  if (isTomorrow) {
    return `Mañana, ${timeString}`;
  }

  // Fallback for any other date (e.g., "01/Oct/2025, 9:00 AM")
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const dateString = date
    .toLocaleDateString("es-ES", dateOptions)
    .replace(/\./g, ""); // Remove periods from short month names

  return `${dateString}, ${timeString}`;
};

interface RegisteredAppointmentsTableProps {
  data: AppointmentsApiResponse;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onNewAppointmentClick: () => void;
  onViewAppointment: (id: string) => void;
  onEditAppointment: (id: string) => void;

  // New props for filter control
  currentStatusFilter: string;
  onStatusFilterChange: (status: string) => void;
  currentTimeFilter: string;
  onTimeFilterChange: (time: string) => void;
}

interface RegisteredAppointmentsTableProps {
  data: AppointmentsApiResponse;
  // Handlers for pagination and other actions would typically be passed down
  onNextPage: () => void;
  onPreviousPage: () => void;
  onNewAppointmentClick: () => void;
  onViewAppointment: (id: string) => void;
  onEditAppointment: (id: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClearSearch?: () => void;
  // Handlers for search/filter changes would also be here (omitted for brevity)
}

const RegisteredAppointmentsTable: React.FC<
  RegisteredAppointmentsTableProps
> = ({
  data,
  onNextPage,
  onPreviousPage,
  onNewAppointmentClick,
  onViewAppointment,
  onEditAppointment,
  currentStatusFilter,
  onStatusFilterChange,
  currentTimeFilter,
  onTimeFilterChange,
  searchQuery, // NEW
  onSearchChange, // NEW
  onClearSearch, // NEW
}) => {
  const appointments = data.results;
  const totalCount = data.count;
  const hasNextPage = !!data.next;
  const hasPreviousPage = !!data.previous;
  const navigate = useNavigate();

  // Optional: Add debounce for search
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchQuery !== searchQuery) {
        onSearchChange(localSearchQuery);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [localSearchQuery, onSearchChange, searchQuery]);

  // **UPDATED** Function to determine the badge class based on the status
  const getStatusBadgeClass = (status: AppointmentStatus): string => {
    switch (status) {
      case "confirmed":
        return "badge accent";
      case "pending":
        // **Change 2: Pending to Yellow/Warning**
        return "badge info";
      case "completed":
        return "badge success";
      case "cancelled":
        // **Change 2: Cancelled to Red/Error**
        return "badge error";
      default:
        return "badge default";
    }
  };

  // Calculate the range of appointments being displayed for the pagination text
  const currentRange = useMemo(() => {
    if (appointments.length === 0) return "Mostrando 0 de 0 citas";

    return `Mostrando 1-${appointments.length} de ${totalCount} citas`;
  }, [appointments.length, totalCount]);

  const handleViewAppointment = (appointmentId: string) => {
    navigate(`/appointments/${appointmentId}`);
  };
  const handleEditAppointment = (appointmentId: string) => {
    navigate(`/appointment/edit/${appointmentId}`);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchQuery(e.target.value);
  };

  // NEW: Handle clear search button
  const handleClearSearchClick = () => {
    setLocalSearchQuery("");
    if (onClearSearch) {
      onClearSearch();
    } else {
      onSearchChange("");
    }
  };

  return (
    <div
      className="container agenda-content full-width-main"
      id="main-content-appointments"
    >
      <div className="main-calendar-view">
        {/* Header Section */}
        <div className="main-calendar-header">
          <h2 id="view-title">Citas Registradas</h2>
          <div>
            <button
              className="btn btn--primary"
              onClick={onNewAppointmentClick}
            >
              <i className="fas fa-calendar-plus"></i> Nueva Cita
            </button>
          </div>
        </div>

        {/* Data Table View */}
        <div id="data-table-view" className="data-table-container">
          {/* Table Controls */}
          <div className="table-controls">
            <input
              type="text"
              placeholder="Buscar por paciente o servicio..."
              className="search-input"
              value={localSearchQuery}
              onChange={handleSearchInputChange}
              style={{ paddingRight: "40px", width: "100%" }}
            />

            {/* Filter by Status */}
            <select
              className="form-group select filter-select"
              aria-label="Filtrar por estado de cita"
              value={currentStatusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
            >
              <option value="all">Todas las Citas</option>
              <option value="confirmed">Confirmadas</option>
              <option value="pending">Pendientes</option>
              <option value="completed">Completadas</option>
              <option value="cancelled">Canceladas</option>
            </select>

            {/* Filter by Date Range */}
            <select
              className="form-group select filter-select"
              aria-label="Filtrar por rango de fecha"
              value={currentTimeFilter}
              onChange={(e) => onTimeFilterChange(e.target.value)}
            >
              <option value="all">Todas las Fechas</option>
              <option value="upcoming">Próximas Citas</option>
              <option value="past">Citas Pasadas</option>
            </select>
          </div>

          {searchQuery && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "0.5rem 1rem",
                backgroundColor: "var(--color-primary)",
                borderRadius: "var(--border-radius)",
                borderLeft: "4px solid var(--color-accent)",
              }}
            >
              <span
                style={{ fontSize: "0.95rem", color: "var(--color-secondary)" }}
              >
                Buscando: "<strong>{searchQuery}</strong>"
                {appointments.length === 0 && " - No se encontraron resultados"}
                {appointments.length > 0 &&
                  ` - ${appointments.length} resultado(s)`}
              </span>
            </div>
          )}

          {/* Table Wrapper */}
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Paciente</th>
                  <th>Título</th>
                  <th>Servicio</th>
                  <th>Fecha y Hora</th>
                  <th>Estado</th>
                  <th className="actions-column">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td data-label="Paciente">
                      {appointment.patient?.name ?? "-"}
                    </td>
                    <td data-label="Título">{appointment.title}</td>
                    <td data-label="Servicio">{appointment.service?.name}</td>
                    <td data-label="Fecha y Hora">
                      {formatAppointmentDateTime(appointment.start_time)}
                    </td>
                    <td data-label="Estado">
                      <span className={getStatusBadgeClass(appointment.status)}>
                        {/* Translate status to Spanish for display */}
                        {appointment.status === "confirmed" && "Confirmada"}
                        {appointment.status === "pending" && "Pendiente"}
                        {appointment.status === "completed" && "Completada"}
                        {appointment.status === "cancelled" && "Cancelada"}
                      </span>
                    </td>
                    <td data-label="Acciones" className="actions-column">
                      <button
                        className="btn btn--secondary btn--small"
                        title="Ver"
                        onClick={() => handleViewAppointment(appointment.id)}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="btn btn--secondary btn--small"
                        title="Editar"
                        onClick={() => handleEditAppointment(appointment.id)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </td>
                  </tr>
                ))}
                {appointments.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No se encontraron citas con los filtros aplicados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <span>{currentRange}</span>
            <button
              className="btn btn--secondary btn--small"
              disabled={!hasPreviousPage}
              onClick={onPreviousPage}
            >
              <i className="fas fa-chevron-left"></i> Anterior
            </button>
            <button
              className="btn btn--secondary btn--small"
              disabled={!hasNextPage}
              onClick={onNextPage}
            >
              Siguiente <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisteredAppointmentsTable;
